import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";



import { useOrderedArticlesStore } from "@/data/stores/orderedArticles";



import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerArticleAttribute } from "@/data/model/Customer";
import { AttributeDisplayType, Language } from "@/data/model/Enums";
import { standardAttributeIds } from "@/data/util/Config";



import { hasAttributeDisplayType } from "@/shared/helpers/AttributeHelper";


// the icon attributes of a location, looked up by article id wherever an item name is rendered
export const useArticleAttributesStore = defineStore("articleAttributes", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const orderedArticles = useOrderedArticlesStore();

    const attributesByArticleId = ref(new Map<string, Array<ICustomerArticleAttribute>>());
    // every enabled attribute of the location in the order the server put them in, which is the order
    // all frontends render them in
    const orderedAttributes = ref<Array<ICustomerArticleAttribute>>([]);
    const loadedLocationId = ref<string | null>(null);
    // the offer, the allocation and the operator settings all ask for the same set when a location is
    // entered, so they share the request that is already on its way instead of each starting its own
    let pendingLoad: { locationId: string; promise: Promise<void> } | undefined;

    function reset() {
        loadedLocationId.value = null;
        attributesByArticleId.value = new Map();
        orderedAttributes.value = [];
    }

    async function load(locationId: string, language: Language) {
        if (loadedLocationId.value === locationId) {
            return;
        }

        if (pendingLoad?.locationId !== locationId) {
            pendingLoad = { locationId: locationId, promise: fetchAttributes(locationId, language) };
        }


        await pendingLoad.promise;
    }

    async function fetchAttributes(locationId: string, language: Language) {
        try {
            await setAttributes(locationId, language);
        } finally {
            if (pendingLoad?.locationId === locationId) {
                pendingLoad = undefined;
            }
        }
    }

    async function setAttributes(locationId: string, language: Language) {
        const result = await customerDao.getArticleAttributes(locationId, language);
        const attributesById = new Map(result.attributes.map((attribute) => [attribute.id, attribute]));
        // the ids of an article come in the order they were linked, so they are put back into the
        // location wide order here
        const rankById = new Map(result.attributes.map((attribute, index) => [attribute.id, index]));

        const byArticleId = new Map<string, Array<ICustomerArticleAttribute>>();
        for (const [articleId, attributeIds] of Object.entries(result.attributeIdsByArticleId)) {
            const attributes = attributeIds
                .map((id) => attributesById.get(id))
                .filter((attribute): attribute is ICustomerArticleAttribute => attribute != null)
                .sort((a, b) => rankById.get(a.id)! - rankById.get(b.id)!);
            byArticleId.set(articleId, attributes);
        }

        attributesByArticleId.value = byArticleId;
        orderedAttributes.value = result.attributes;
        loadedLocationId.value = locationId;
    }

    // "Ordered before" only where the user picks from the offer: on a cart, a bill or an order every item is ordered by
    // definition, so the icon would sit on every row there. flags and filters are shown on the offer only anyway.
    // an option is passed with the top-level main item it is offered in, see OrderedArticlesStore.isOrdered
    function iconAttributesForArticle(
        articleId: string | undefined,
        includeOrderedBefore: boolean = false,
        mainArticleId?: string
    ): Array<ICustomerArticleAttribute> {
        return attributesForArticle(articleId, AttributeDisplayType.Icon, includeOrderedBefore, mainArticleId);
    }

    function flagAttributesForArticle(
        articleId: string | undefined,
        mainArticleId?: string
    ): Array<ICustomerArticleAttribute> {
        return attributesForArticle(articleId, AttributeDisplayType.Flag, true, mainArticleId);
    }

    function filterAttributesForArticle(articleId: string | undefined): Array<ICustomerArticleAttribute> {
        return attributesForArticle(articleId, AttributeDisplayType.Filter, true);
    }

    // the attributes linked to the article, plus "Ordered before" when the user has ordered it - that one is linked to
    // no article, so it comes from the location wide list, displayed the way the location has set it up
    function attributesForArticle(
        articleId: string | undefined,
        displayType: AttributeDisplayType,
        includeOrderedBefore: boolean,
        mainArticleId?: string
    ): Array<ICustomerArticleAttribute> {
        if (articleId == null) {
            return [];
        }
        const attributes =
            attributesByArticleId.value
                .get(articleId)
                ?.filter((c) => hasAttributeDisplayType(c.displayType, displayType)) ?? [];

        if (!includeOrderedBefore || !orderedArticles.isOrdered(articleId, mainArticleId)) {
            return attributes;
        }

        const orderedBefore = orderedAttributes.value.find(
            (c) => c.id === standardAttributeIds.orderedBefore && hasAttributeDisplayType(c.displayType, displayType)
        );
        return orderedBefore == null ? attributes : [...attributes, orderedBefore];
    }

    return {
        load,
        reset,
        iconAttributesForArticle,
        flagAttributesForArticle,
        filterAttributesForArticle,
        attributesByArticleId,
        orderedAttributes,
    };
});

export type ArticleAttribute = ReturnType<typeof useArticleAttributesStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useArticleAttributesStore, import.meta.hot));
}
